/**
 * Used on any async method to ensure that it will only be triggered once at a
 * time with the given parameters.
 */
export const nonConcurrent = (cacheKeys: number = 10) => {
  return (target: {}, propertyKey: string, descriptor: PropertyDescriptor) => {
    // Create a unique symbol, which we're going to use to create a cache of
    // currently running calls on the instance of the class
    const cacheSymbol = Symbol("@use/decorators.nonConcurrent.cache");

    return {
      // Forward configurable and enumerable "as is"
      configurable: descriptor.configurable,
      enumerable: descriptor.enumerable,

      // Now override the method signature, to ensure that it will only be
      // once at a time
      async value(...args: unknown[]): Promise<any> {
        // tslint:disable-line no-any
        // Create a strongly typed version of the current execution
        // context, so that we can apply / extract cache from it
        // @ts-expect-error
        const context = this as unknown & {
          [cacheSymbol]: Map<unknown, unknown>;
        };

        // Prepare cache on the original method
        const cache = (context[cacheSymbol] =
          context[cacheSymbol] || new Map());

        // Generate the id that we're going to cache this execution
        // under
        const cacheId = generateCacheId(cacheKeys, ...args);

        // If an execution with this cacheId is already running, then
        // return the existing promise to prevent concurrency!
        if (cache.has(cacheId)) {
          return cache.get(cacheId);
        }

        // If we get here, there are no executions currently running
        // under the generated id - dispatch a new execution!
        // tslint:disable-next-line no-invalid-this
        const promise = <Promise<unknown | void>>(
          (<(...args: unknown[]) => unknown>descriptor.value).call(
            this,
            ...args
          )
        );

        // Cache the promise returned by the original method, so that we
        // can return it in case the method is going to be triggered
        // again
        cache.set(cacheId, promise);

        try {
          // Wait for the promise of the original method to resolve, and
          // then return the value directly forward
          return await promise;
        } catch (err) {
          // Propagate any errors thrown by the original method
          throw err;
        } finally {
          // Once we're done with the original request, make sure that the
          // cached promise is cleaned up (this will allow future
          // executions to be processed)
          cache.delete(cacheId);
        }
      },
    };
  };
};

/**
 * Internal helper that translates given arguments into the cacheId that should
 * be used when determining if a request to the function is already running.
 */
function generateCacheId(cacheKeys: number, ...args: unknown[]): string {
  // Create an array of argument values, so that we can combine each of the
  // requested values
  const keys: string[] = [];

  // Iterate over the desired keys, and generate combined output
  for (let i = 0; i < cacheKeys && i < args.length; i++) {
    // Resolve value
    const arg =
      !args[i] || typeof args[i] !== "object"
        ? args[i]
        : JSON.stringify(args[i]);
    // Push value to list of keys
    keys.push(`${i}=${arg}`);
  }

  // Return the id publicly
  return keys.join("&");
}
